
interface Property {
  id: number;
  title: string;
  type: string;
  price: number;
  location: string;
  bedrooms: number;
  bathrooms: number;
  area: number;
  image: string;
  tag: string | null;
  features: string[];
  broker: string;
}

interface PropertyCardProps {
  property: Property;
}

const PropertyCard = ({ property }: PropertyCardProps) => {
  const formatPrice = (price: number) => {
    return price.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const getTagColor = (tag: string | null) => {
    switch (tag) {
      case 'Novo':
        return 'bg-emerald-500';
      case 'Destaque':
        return 'bg-amber-500';
      case 'Oportunidade':
        return 'bg-rose-500';
      default:
        return '';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md overflow-hidden group hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
      {/* Imagem */}
      <div className="relative h-56 overflow-hidden">
        <img
          src={property.image}
          alt={property.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/20"></div>
        
        {/* Tag */}
        {property.tag && (
          <span className={`absolute top-4 left-4 px-3 py-1 rounded-full text-white text-xs font-semibold ${getTagColor(property.tag)}`}>
            {property.tag}
          </span>
        )}
        
        {/* Favorito */}
        <button className="absolute top-4 right-4 w-9 h-9 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors cursor-pointer">
          <i className="ri-heart-line text-gray-600 hover:text-rose-500 transition-colors"></i>
        </button>
      </div>

      {/* Informações */}
      <div className="p-5">
        <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
          {formatPrice(property.price)}
        </div>
        
        <h3 className="text-base font-medium text-gray-800 dark:text-gray-100 mb-2 line-clamp-2 min-h-[48px]">
          {property.title}
        </h3>
        
        <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 text-sm mb-4">
          <i className="ri-map-pin-line text-teal-600"></i>
          <span className="truncate">{property.location}</span>
        </div>

        {/* Características */}
        <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-300 pb-4 border-b border-gray-100 dark:border-gray-700">
          {property.bedrooms > 0 && (
            <div className="flex items-center gap-1.5">
              <i className="ri-hotel-bed-line text-gray-400 dark:text-gray-500"></i>
              <span className="font-medium">{property.bedrooms}</span>
            </div>
          )}
          {property.bathrooms > 0 && (
            <div className="flex items-center gap-1.5">
              <i className="ri-drop-line text-gray-400 dark:text-gray-500"></i>
              <span className="font-medium">{property.bathrooms}</span>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <i className="ri-ruler-line text-gray-400 dark:text-gray-500"></i>
            <span className="font-medium">{property.area}m²</span>
          </div>
        </div>

        {/* Rodapé */}
        <div className="flex items-center justify-between pt-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-teal-100 dark:bg-teal-900/50 rounded-full flex items-center justify-center">
              <i className="ri-user-line text-teal-600 text-sm"></i>
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400">{property.broker}</span>
          </div>
          <a
            href={`https://wa.me/5511922305730?text=Olá,%20Daniel!%20Tenho%20interesse%20no%20imóvel:%20${encodeURIComponent(property.title)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 border border-teal-600 text-teal-600 rounded-full text-xs font-semibold hover:bg-teal-600 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
          >
            Ver Detalhes
          </a>
        </div>
      </div>
    </div>
  );
};

export default PropertyCard;
